import { AxiosInstance } from 'axios';
import { Result } from 'services/Result';
import i18next from 'i18next';
import { OrderDetails, ShopCertificateResponse } from 'types/pricing';

type TokenHandlerResponse =
  | {
      status: 'tokenReturned';
      token: string;
      isSubscriptionPurchased: false;
      errorMessage?: undefined;
    }
  | {
      status: 'success';
      token?: undefined;
      isSubscriptionPurchased: true;
      orderDetails: OrderDetails;
      errorMessage?: undefined;
    }
  | {
      status: 'error';
      token?: undefined;
      isSubscriptionPurchased: false;
      errorMessage: {
        title: string;
        status: number;
        detail: string;
        Exception: string;
      };
    };

export class WebshopService {
  private axiosInstance: AxiosInstance;
  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  getShopCertificationTests = async () => {
    const response = await this.axiosInstance.get<Result<ShopCertificateResponse>>(`api/shop-certification-tests`);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data.shopCertificationTests;
    }
    throw Error('There was an internal error while fetching certificates.');
  };

  tokenHandler = async (
    tokenId: string,
    email: string,
    planCode: string,
    currency: string,
    couponCode: string,
    productIds: string[],
    includeSubscription: boolean
  ): Promise<TokenHandlerResponse> => {
    const response = await this.axiosInstance.post(`/api/Purchase`, {
      TokenId: tokenId,
      Email: email,
      PlanCode: planCode,
      IncludeSubscription: includeSubscription,
      Currency: currency,
      CouponCode: couponCode,
      ProductIds: productIds,
    });
    if (response.data) {
      if (response.status === 406) {
        //if 406 is returned it means that 3D secure handling is required and we need to save params
        return {
          status: 'tokenReturned',
          token: response.data,
          isSubscriptionPurchased: false,
        }; //threeDSecureActionTokenId is needed to start 3D Secure challenge
      } else if (!response.data.isError && response.data.data) {
        return {
          status: 'success',
          isSubscriptionPurchased: true,
          orderDetails: {
            closedAt: response.data.data.closedAt,
            email: response.data.data.email,
            invoiceNumber: response.data.data.invoiceNumber,
          },
        };
      } else {
        return {
          status: 'error',
          isSubscriptionPurchased: false,
          errorMessage: response.data.detail,
        };
      }
    }

    throw Error(i18next.t('3DSecureTokenError', { ns: 'organizationPage' }));
  };

  sendThreeDSecureRequest = async (
    tokenId: string,
    email: string,
    planCode: string,
    currency: string,
    couponCode: string,
    threeDSecureTokenId: string,
    productIds: string[],
    includeSubscription: boolean
  ) => {
    const response = await this.axiosInstance.post(`/api/Purchase`, {
      TokenId: tokenId,
      Email: email,
      PlanCode: planCode,
      IncludeSubscription: includeSubscription,
      Currency: currency,
      CouponCode: couponCode,
      ThreeDSecureToken: threeDSecureTokenId,
      ProductIds: productIds,
    });
    if (response.data) {
      return response;
    }

    throw Error(i18next.t('3DSecureRequestError', { ns: 'organizationPage' }));
  };

  getSubscriptionPrice = async (planCode: string): Promise<number> => {
    const response = await this.axiosInstance.get<Result<number>>(`api/Payment/GetSubscriptionPrice?planCode=${planCode}`);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error(i18next.t('GetSubscriptionPriceError', { ns: 'organizationPage' }));
  };
}
